import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  QueryDefinition,
} from '@reduxjs/toolkit/query';
import { getAuth } from 'firebase/auth';
import { getBaseApiUrl } from 'src/config/apiConfig';

export interface StreamingResponse<TChunk> {
  chunks: TChunk[];
  isStreaming: boolean;
}

interface CreateStreamingQueryOptions<TArg> {
  /** Builds the API-relative URL for a query arg, e.g. `gateway/${arg.imei}/x`. */
  url: (arg: TArg) => string;

  /** HTTP method, defaults to GET. */
  method?: 'GET' | 'POST';

  /** Optional JSON request body, built from the query arg. */
  body?: (arg: TArg) => unknown;
}

type CreateStreamingQueryOutput<TArg, TChunk> = QueryDefinition<
  TArg,
  BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError, {}>,
  any,
  StreamingResponse<TChunk>
>;

/**
 * Creates an RTK Query streaming endpoint that fetches the URL itself with the
 * user's bearer token and appends each newline-delimited JSON line of the
 * response to the cache entry's `chunks` as it arrives, with `isStreaming`
 * cleared once the response ends or breaks. The fetch is aborted when the
 * entry leaves the cache.
 *
 * Each distinct query arg gets its own cache entry (and thus its own stream);
 * include a nonce in the arg to force a fresh stream for repeat requests.
 *
 * See: https://redux-toolkit.js.org/rtk-query/usage/streaming-updates
 */
export function createStreamingQuery<TArg, TChunk = any>({
  url,
  method = 'GET',
  body,
}: CreateStreamingQueryOptions<TArg>): {
  queryFn: NonNullable<CreateStreamingQueryOutput<TArg, TChunk>['queryFn']>;
  onCacheEntryAdded: NonNullable<
    CreateStreamingQueryOutput<TArg, TChunk>['onCacheEntryAdded']
  >;
} {
  return {
    queryFn: () => ({
      data: { chunks: [], isStreaming: true } as StreamingResponse<TChunk>,
    }),
    async onCacheEntryAdded(
      arg,
      { updateCachedData, cacheDataLoaded, cacheEntryRemoved }
    ) {
      try {
        await cacheDataLoaded;

        const target = `${getBaseApiUrl().replace(/\/$/, '')}/${url(arg)}`;
        const token = (await getAuth()?.currentUser?.getIdToken?.()) ?? '';

        const abortController = new AbortController();
        cacheEntryRemoved.then(() => abortController.abort());

        const response = await fetch(target, {
          method,
          signal: abortController.signal,
          headers: {
            ...(token && { authorization: `Bearer ${token}` }),
            ...(body && { 'content-type': 'application/json' }),
          },
          ...(body && { body: JSON.stringify(body(arg)) }),
        });
        if (!response.ok) {
          throw new Error(`Streaming request failed (${response.status})`);
        }
        if (!response.body) {
          throw new Error(`No response body from ${target}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        try {
          while (!abortController.signal.aborted) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';

            for (const line of lines) {
              if (line.trim()) {
                try {
                  const chunk = JSON.parse(line) as TChunk;
                  updateCachedData((draft) => {
                    draft.chunks.push(chunk as any);
                  });
                } catch (e) {
                  console.error('Failed to parse chunk:', line, e);
                }
              }
            }
          }

          updateCachedData((draft) => {
            draft.isStreaming = false;
          });
        } catch (error) {
          if (error instanceof Error && error.name !== 'AbortError') {
            updateCachedData((draft) => {
              draft.isStreaming = false;
            });
            throw error;
          }
        } finally {
          reader.releaseLock();
        }
      } catch (error) {
        console.error('Streaming error:', error);
        updateCachedData((draft) => {
          draft.isStreaming = false;
        });
      }
    },
  };
}
